import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Search, X, Check } from 'lucide-react';
import { SHEET_ANIMATION, BACKDROP_ANIMATION } from './sheetAnimationConfig';

/**
 * CountrySheet - Bottom sheet for country selection
 * Provides searchable list with flags and multi-select support
 * 
 * @param {boolean} isOpen - Whether sheet is open
 * @param {Function} onClose - Callback to close sheet
 * @param {Array|string} selectedCountries - Selected country code(s)
 * @param {Function} onCountrySelect - Callback when countries are selected
 * @param {Array} countries - List of available countries
 * @param {boolean} multiSelect - Allow multiple country selection
 * @param {string} title - Sheet title
 */
const CountrySheet = ({
  isOpen,
  onClose,
  selectedCountries,
  onCountrySelect,
  countries = [],
  multiSelect = false,
  title = 'Select Country'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);

  // Normalize selected countries to array
  useEffect(() => {
    if (selectedCountries) {
      const normalized = Array.isArray(selectedCountries) 
        ? selectedCountries 
        : [selectedCountries];
      setSelectedItems(normalized);
    } else {
      setSelectedItems([]);
    }
  }, [selectedCountries]);

  // Default countries if none provided
  const defaultCountries = useMemo(() => [
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', popular: true },
    { code: 'GB', name: 'England', flag: '🏴󐁧󐁢󐁥󐁮󐁧󐁿', popular: true },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', popular: true },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', popular: true },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', popular: true },
    { code: 'FR', name: 'France', flag: '🇫🇷', popular: true },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  ], []);

  const countryList = countries.length > 0 ? countries : defaultCountries;

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countryList;
    
    const query = searchQuery.toLowerCase();
    return countryList.filter(country => 
      country.name.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  }, [countryList, searchQuery]);

  // Group countries by popular/all
  const groupedCountries = useMemo(() => {
    const popular = filteredCountries.filter(c => c.popular);
    const others = filteredCountries.filter(c => !c.popular);
    
    return { popular, others };
  }, [filteredCountries]);

  const handleCountryToggle = (countryCode) => {
    if (multiSelect) {
      const newSelection = selectedItems.includes(countryCode)
        ? selectedItems.filter(c => c !== countryCode)
        : [...selectedItems, countryCode];
      
      setSelectedItems(newSelection);
    } else {
      setSelectedItems([countryCode]);
      onCountrySelect(countryCode);
      onClose();
    }
  };

  const handleApply = () => {
    onCountrySelect(multiSelect ? selectedItems : selectedItems[0]);
    onClose();
  };

  const handleSelectAll = () => {
    if (selectedItems.length === countryList.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(countryList.map(c => c.code));
    }
  };

  const isSelected = (countryCode) => selectedItems.includes(countryCode);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            {...BACKDROP_ANIMATION}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          
          {/* Sheet */}
          <motion.div
            {...SHEET_ANIMATION}
            className="fixed bottom-0 left-0 right-0 bg-gray-900 rounded-t-3xl z-50 max-h-[85vh] overflow-hidden"
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-2">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Globe size={20} className="text-emerald-400" />
                {title}
              </h3>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <X size={20} className="text-white/70" />
              </button>
            </div>
            
            {/* Search bar */}
            <div className="px-6 py-4 border-b border-white/10">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search countries..."
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 
                           rounded-xl text-white placeholder-white/50 
                           focus:bg-white/15 focus:border-emerald-500/50 
                           focus:outline-none transition-all"
                />
              </div>
              
              {multiSelect && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm text-white/60">
                    {selectedItems.length} selected
                  </span>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    {selectedItems.length === countryList.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              )}
            </div>
            
            {/* Country list */}
            <div className="overflow-y-auto max-h-[50vh]">
              {groupedCountries.popular.length > 0 && (
                <>
                  <div className="px-6 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
                    Popular
                  </div>
                  {groupedCountries.popular.map(country => (
                    <button
                      key={country.code}
                      onClick={() => handleCountryToggle(country.code)}
                      className={`
                        w-full px-6 py-3 flex items-center justify-between
                        transition-colors hover:bg-white/5
                        ${isSelected(country.code) ? 'bg-emerald-500/10' : ''}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{country.flag}</span>
                        <div className="text-left">
                          <div className="text-white font-medium">{country.name}</div>
                          <div className="text-xs text-white/50">{country.code}</div>
                        </div>
                      </div>
                      {isSelected(country.code) && (
                        <Check size={18} className="text-emerald-400" />
                      )}
                    </button>
                  ))}
                </>
              )}
              
              {groupedCountries.others.length > 0 && (
                <>
                  <div className="px-6 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
                    {groupedCountries.popular.length > 0 ? 'All Countries' : 'Countries'}
                  </div>
                  {groupedCountries.others.map(country => (
                    <button
                      key={country.code}
                      onClick={() => handleCountryToggle(country.code)}
                      className={`
                        w-full px-6 py-3 flex items-center justify-between
                        transition-colors hover:bg-white/5
                        ${isSelected(country.code) ? 'bg-emerald-500/10' : ''}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{country.flag}</span>
                        <div className="text-left">
                          <div className="text-white font-medium">{country.name}</div>
                          <div className="text-xs text-white/50">{country.code}</div>
                        </div>
                      </div>
                      {isSelected(country.code) && (
                        <Check size={18} className="text-emerald-400" />
                      )}
                    </button>
                  ))}
                </>
              )}
              
              {filteredCountries.length === 0 && (
                <div className="px-6 py-12 text-center">
                  <Globe size={48} className="mx-auto mb-3 text-white/20" />
                  <p className="text-white/50">No countries found</p>
                </div>
              )}
            </div>
            
            {/* Apply button for multi-select */}
            {multiSelect && (
              <div className="px-6 py-4 border-t border-white/10">
                <button
                  onClick={handleApply}
                  disabled={selectedItems.length === 0}
                  className="w-full py-3 bg-emerald-500 text-white rounded-xl 
                           font-semibold hover:bg-emerald-600 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Selection
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CountrySheet;